import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type CSSProperties,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  CheckCircle,
  ChevronRight,
  Cloud,
  CloudOff,
  HardDrive,
  ImagePlus,
  Mic,
  Sparkles,
  MapPin,
  TrendingDown,
  TrendingUp,
  Minus,
  X,
} from 'lucide-react';
import { BodyMap } from '@/components/checkup/BodyMap';
import {
  buildZonesForOptions,
  getRequiredVariants,
  type MacroVariant,
} from '@/components/checkup/bodyMapMacroZones';
import type { SessionType, SessionStatus, FieldType } from 'shared';
import { api, ApiError } from '@/lib/api';
import { saveDraft, getDraft, deleteDraft, type DraftEntry } from '@/lib/offline';
import { useOnline } from '@/hooks/useOnline';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Skeleton, SkeletonText } from '@/components/ui/Spinner';
import { cn } from '@/lib/cn';

/* ==========================================================================
   S4 — Checkup Compilation (most critical page)

   Dynamic form rendering from form_fields, accordion sections,
   autosave on blur, progress tracking, measurement sliders with deltas.
   ========================================================================== */

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

interface FormFieldOption {
  value: string;
  label: string;
}

interface ValidationRules {
  min?: number;
  max?: number;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
}

interface FormField {
  id: number;
  fieldKey: string;
  label: string;
  fieldType: FieldType;
  section: string;
  sectionOrder: number;
  fieldOrder: number;
  isRequired: boolean;
  options?: FormFieldOption[];
  validationRules?: ValidationRules;
  placeholder?: string;
  helpText?: string;
}

interface CheckupData {
  id: number;
  clientId: number;
  clientName: string;
  sessionType: SessionType;
  status: SessionStatus;
  answers: Record<string, unknown>;
  measurements?: MeasurementData[];
  createdAt: string;
  completedAt?: string | null;
}

interface MeasurementData {
  key: string;
  label: string;
  value: number | null;
  previousValue: number | null;
  unit: string;
  min: number;
  max: number;
}

interface SectionInfo {
  name: string;
  fields: FormField[];
  order: number;
}

/* --------------------------------------------------------------------------
   Constants
   -------------------------------------------------------------------------- */

const SESSION_TYPE_LABELS: Record<SessionType, string> = {
  welcome: 'Welcome',
  beauty: 'Beauty',
  kinesiology: 'Kinesiologia',
  medical: 'Medico',
};

const AUTOSAVE_DEBOUNCE_MS = 800;

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export function CheckupCompilation() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isOnline } = useOnline();

  const [checkup, setCheckup] = useState<CheckupData | null>(null);
  const [formFields, setFormFields] = useState<FormField[]>([]);
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [measurements, setMeasurements] = useState<MeasurementData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error' | 'local'>('idle');
  const [isCompleting, setIsCompleting] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('');
  const [restoredFromLocal, setRestoredFromLocal] = useState(false);

  const saveTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const saveStatusTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const localSaveTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // ------------------------------------------------------------------
  // Fetch data
  // ------------------------------------------------------------------

  const loadData = useCallback(async () => {
    if (!id) return;
    const sessionId = parseInt(id, 10);
    setIsLoading(true);
    setError(null);

    try {
      const checkupData = await api.get<CheckupData>(`/checkups/${id}`);
      setCheckup(checkupData);

      // Check for local draft and use it if newer than server data
      let usedLocalDraft = false;
      try {
        const localDraft = await getDraft(sessionId);
        if (localDraft && !localDraft.synced) {
          // Local draft exists and hasn't been synced — compare timestamps
          const serverTime = new Date(checkupData.createdAt).getTime();
          if (localDraft.updatedAt > serverTime) {
            // Local draft is newer — restore it
            setAnswers(localDraft.answers);
            if (localDraft.measurements) {
              // Merge measurement values from local draft into server measurement structure
              const mergedMeasurements = (checkupData.measurements ?? []).map((m) => ({
                ...m,
                value: localDraft.measurements?.[m.key] ?? m.value,
              }));
              setMeasurements(mergedMeasurements);
            } else {
              setMeasurements(checkupData.measurements ?? []);
            }
            setRestoredFromLocal(true);
            usedLocalDraft = true;
          }
        }
      } catch {
        // IndexedDB error — proceed with server data only
      }

      if (!usedLocalDraft) {
        setAnswers(checkupData.answers ?? {});
        setMeasurements(checkupData.measurements ?? []);
      }

      // Load form fields for this session type + client data in parallel
      const [fields, clientData] = await Promise.all([
        api.get<FormField[]>(
          `/form-fields?sessionType=${checkupData.sessionType}`,
        ),
        api
          .get<{
            nome?: string;
            cognome?: string;
            dataNascita?: string | null;
            telefono?: string | null;
            email?: string | null;
            fonte?: string | null;
          }>(`/clients/${checkupData.clientId}`)
          .catch(() => null),
      ]);
      setFormFields(fields);

      // Pre-fill dei dati personali dalla scheda cliente: l'operatrice non
      // deve riscrivere nome/cognome/email/ecc. che gia' esistono. Sovrascrivo
      // SOLO le risposte vuote (rispetto modifiche manuali e local draft).
      if (clientData) {
        const prefill: Record<string, unknown> = {
          nome: clientData.nome,
          cognome: clientData.cognome,
          data_nascita: clientData.dataNascita,
          telefono: clientData.telefono,
          email: clientData.email,
          presentato_da: clientData.fonte,
        };
        setAnswers((prev) => {
          const next = { ...prev };
          for (const [key, val] of Object.entries(prefill)) {
            const cur = next[key];
            if (val != null && val !== '' && (cur == null || cur === '')) {
              next[key] = val;
            }
          }
          return next;
        });
      }

      // Set the first section as active tab
      if (fields.length > 0) {
        const sorted = [...fields].sort((a, b) => a.sectionOrder - b.sectionOrder);
        setActiveTab(sorted[0].section);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.status === 404 ? 'Check-up non trovato.' : err.message);
      } else {
        setError('Errore di connessione. Riprova.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Cleanup timers
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      if (saveStatusTimerRef.current) clearTimeout(saveStatusTimerRef.current);
      if (localSaveTimerRef.current) clearTimeout(localSaveTimerRef.current);
    };
  }, []);

  // ------------------------------------------------------------------
  // Sections derived from form fields
  // ------------------------------------------------------------------

  const sections: SectionInfo[] = useMemo(() => {
    const map = new Map<string, SectionInfo>();

    for (const field of formFields) {
      if (!map.has(field.section)) {
        map.set(field.section, {
          name: field.section,
          fields: [],
          order: field.sectionOrder,
        });
      }
      map.get(field.section)!.fields.push(field);
    }

    // Sort sections by order, fields by fieldOrder
    const result = Array.from(map.values()).sort((a, b) => a.order - b.order);
    for (const section of result) {
      section.fields.sort((a, b) => a.fieldOrder - b.fieldOrder);
    }

    return result;
  }, [formFields]);

  // Available section tabs
  const sectionTabs = useMemo(
    () => sections.map((s) => s.name),
    [sections],
  );

  // ------------------------------------------------------------------
  // Progress calculation
  // ------------------------------------------------------------------

  const { completedSections, totalSections, allRequiredFilled } = useMemo(() => {
    let completed = 0;
    let total = sections.length;
    let allFilled = true;

    for (const section of sections) {
      const requiredFields = section.fields.filter((f) => f.isRequired);
      const allSectionFilled = requiredFields.every((f) => {
        const val = answers[f.fieldKey];
        if (val === undefined || val === null || val === '') return false;
        if (Array.isArray(val) && val.length === 0) return false;
        return true;
      });
      if (allSectionFilled && requiredFields.length > 0) {
        completed++;
      }
      if (!allSectionFilled && requiredFields.length > 0) {
        allFilled = false;
      }
    }

    return { completedSections: completed, totalSections: total, allRequiredFilled: allFilled };
  }, [sections, answers]);

  // ------------------------------------------------------------------
  // IndexedDB local save
  // ------------------------------------------------------------------

  const saveToLocal = useCallback(
    async (newAnswers: Record<string, unknown>, newMeasurements?: MeasurementData[]) => {
      if (!id || !checkup) return;
      const sessionId = parseInt(id, 10);

      // Build measurements record for storage
      const measRecord: Record<string, number | null> = {};
      const meases = newMeasurements ?? measurements;
      for (const m of meases) {
        measRecord[m.key] = m.value;
      }

      const draft: DraftEntry = {
        sessionId,
        clientId: checkup.clientId,
        sessionType: checkup.sessionType,
        answers: newAnswers,
        measurements: measRecord,
        updatedAt: Date.now(),
        synced: false,
      };

      try {
        await saveDraft(draft);
      } catch {
        // IndexedDB error — not critical, server save is the primary path
      }
    },
    [id, checkup, measurements],
  );

  // ------------------------------------------------------------------
  // Autosave (server + local)
  // ------------------------------------------------------------------

  const performSave = useCallback(
    async (newAnswers: Record<string, unknown>) => {
      if (!id) return;

      // Always save locally first (fast, works offline)
      await saveToLocal(newAnswers);

      if (!navigator.onLine) {
        // Offline — show local save indicator
        setSaveStatus('local');
        if (saveStatusTimerRef.current) clearTimeout(saveStatusTimerRef.current);
        saveStatusTimerRef.current = setTimeout(() => setSaveStatus('idle'), 2000);
        return;
      }

      setSaveStatus('saving');

      try {
        await api.patch(`/checkups/${id}`, { answers: newAnswers });
        setSaveStatus('saved');
        // Mark local draft as synced
        try {
          const sessionId = parseInt(id, 10);
          const existing = await getDraft(sessionId);
          if (existing) {
            await saveDraft({ ...existing, synced: true });
          }
        } catch {
          // IndexedDB error — not critical
        }
        if (saveStatusTimerRef.current) clearTimeout(saveStatusTimerRef.current);
        saveStatusTimerRef.current = setTimeout(() => setSaveStatus('idle'), 2000);
      } catch {
        setSaveStatus('error');
        if (saveStatusTimerRef.current) clearTimeout(saveStatusTimerRef.current);
        saveStatusTimerRef.current = setTimeout(() => setSaveStatus('idle'), 3000);
      }
    },
    [id, saveToLocal],
  );

  const debouncedSave = useCallback(
    (newAnswers: Record<string, unknown>) => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        performSave(newAnswers);
      }, AUTOSAVE_DEBOUNCE_MS);

      // Save to local immediately (no debounce for IndexedDB — it's fast)
      if (localSaveTimerRef.current) clearTimeout(localSaveTimerRef.current);
      localSaveTimerRef.current = setTimeout(() => {
        void saveToLocal(newAnswers);
      }, 200);
    },
    [performSave, saveToLocal],
  );

  // ------------------------------------------------------------------
  // Answer update handler
  // ------------------------------------------------------------------

  const updateAnswer = useCallback(
    (fieldKey: string, value: unknown) => {
      setAnswers((prev) => {
        const next = { ...prev, [fieldKey]: value };
        debouncedSave(next);
        return next;
      });
    },
    [debouncedSave],
  );

  // Immediate save (on blur)
  const saveOnBlur = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    performSave(answers);
  }, [performSave, answers]);

  // ------------------------------------------------------------------
  // Measurement update
  // ------------------------------------------------------------------

  const updateMeasurement = useCallback(
    (key: string, value: number | null) => {
      setMeasurements((prev) => {
        const next = prev.map((m) =>
          m.key === key ? { ...m, value } : m,
        );
        // Include measurements in the save
        const measAnswers = { ...answers };
        for (const m of next) {
          measAnswers[`measurement_${m.key}`] = m.value;
        }
        debouncedSave(measAnswers);
        return next;
      });
    },
    [answers, debouncedSave],
  );

  // ------------------------------------------------------------------
  // Complete checkup
  // ------------------------------------------------------------------

  const handleComplete = useCallback(async () => {
    if (!id) return;
    const sessionId = parseInt(id, 10);
    setIsCompleting(true);
    try {
      // Final save
      await api.patch(`/checkups/${id}`, { answers });
      await api.post(`/checkups/${id}/complete`);
      setCheckup((prev) => prev ? { ...prev, status: 'completed' } : prev);

      // Clear local draft — data is safely on the server
      try {
        await deleteDraft(sessionId);
      } catch {
        // IndexedDB error — not critical
      }
    } catch {
      // Error handled silently — user can retry
    } finally {
      setIsCompleting(false);
    }
  }, [id, answers]);

  // ------------------------------------------------------------------
  // Loading
  // ------------------------------------------------------------------

  if (isLoading) {
    return (
      <div className="flex h-dvh flex-col bg-surface-base">
        <CheckupHeaderSkeleton />
        <div className="flex-1 overflow-y-auto px-5 py-6 mx-auto w-full max-w-3xl">
          <Skeleton height={40} className="w-full mb-6 rounded-[var(--radius-md)]" />
          <div className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="space-y-3">
                <Skeleton height={20} className="w-2/5" />
                <SkeletonText lines={2} />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ------------------------------------------------------------------
  // Error
  // ------------------------------------------------------------------

  if (error || !checkup) {
    return (
      <div className="flex h-dvh flex-col bg-surface-base">
        <header
          className={cn(
            'sticky top-0 z-[var(--z-sticky)]',
            'flex items-center gap-3 h-16 px-5',
            'bg-surface-base/80 backdrop-blur-lg',
            'border-b border-border-default',
          )}
        >
          <button
            onClick={() => navigate(-1)}
            className={cn(
              'flex items-center justify-center',
              'h-[var(--touch-min)] w-[var(--touch-min)]',
              '-ml-2 rounded-[var(--radius-md)]',
              'text-text-primary cursor-pointer',
              'hover:bg-surface-sunken',
            )}
            aria-label="Indietro"
          >
            <ArrowLeft className="h-5 w-5" strokeWidth={2} />
          </button>
          <h1 className="flex-1 font-[family-name:var(--font-heading)] text-lg font-bold text-text-primary">
            Check-up
          </h1>
        </header>
        <div className="flex-1 flex items-center justify-center px-5">
          <div className="text-center">
            <p className="text-lg font-[family-name:var(--font-heading)] font-semibold text-text-primary">
              {error ?? 'Check-up non trovato'}
            </p>
            <Button variant="secondary" className="mt-4" onClick={() => navigate(-1)}>
              Torna indietro
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ------------------------------------------------------------------
  // Main render
  // ------------------------------------------------------------------

  const isCompleted = checkup.status === 'completed';

  return (
    <div className="flex h-dvh flex-col bg-surface-base">
      {/* ---- Fixed header ---- */}
      <header
        className={cn(
          'sticky top-0 z-[var(--z-sticky)]',
          'bg-surface-base/80 backdrop-blur-lg',
          'border-b border-border-default',
        )}
      >
        <div className="flex items-center gap-3 h-16 px-5 mx-auto max-w-3xl">
          {/* Back */}
          <button
            onClick={() => navigate(-1)}
            className={cn(
              'flex items-center justify-center',
              'h-[var(--touch-min)] w-[var(--touch-min)]',
              '-ml-2 rounded-[var(--radius-md)]',
              'text-text-primary cursor-pointer',
              'transition-colors duration-[var(--duration-fast)]',
              'hover:bg-surface-sunken active:bg-border-default',
            )}
            aria-label="Indietro"
          >
            <ArrowLeft className="h-5 w-5" strokeWidth={2} />
          </button>

          {/* Client name + type */}
          <div className="flex-1 min-w-0">
            <p className="font-[family-name:var(--font-heading)] text-base font-bold text-text-primary truncate">
              {checkup.clientName}
            </p>
            <div className="flex items-center gap-2 mt-0.5">
              <Badge status="info" size="sm">
                {SESSION_TYPE_LABELS[checkup.sessionType]}
              </Badge>
              <span className="text-xs text-text-muted">
                {completedSections}/{totalSections} sezioni
              </span>
            </div>
          </div>

          {/* Autosave indicator */}
          <SaveIndicator status={saveStatus} />
        </div>

        {/* Progress bar */}
        <div className="h-0.5 bg-surface-sunken">
          <div
            className="h-full bg-accent-primary transition-all duration-[var(--duration-normal)] ease-[var(--ease-out)]"
            style={{
              width: totalSections > 0
                ? `${(completedSections / totalSections) * 100}%`
                : '0%',
            }}
          />
        </div>
      </header>

      {/* ---- Tab bar (horizontal scrollable) ---- */}
      {sectionTabs.length > 1 && (
        <div
          className={cn(
            'sticky top-[calc(4rem+2px)] z-[var(--z-sticky)]',
            'bg-surface-base/80 backdrop-blur-lg',
            'border-b border-border-default',
          )}
        >
          <div className="flex gap-1 overflow-x-auto px-5 py-2 scrollbar-hide mx-auto max-w-3xl">
            {sectionTabs.map((tab) => {
              const isActive = activeTab === tab;
              const section = sections.find((s) => s.name === tab);
              const requiredFields = section?.fields.filter((f) => f.isRequired) ?? [];
              const allFilled = requiredFields.length > 0 && requiredFields.every((f) => {
                const val = answers[f.fieldKey];
                return val !== undefined && val !== null && val !== '' && !(Array.isArray(val) && val.length === 0);
              });

              return (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={cn(
                    'shrink-0 flex items-center gap-1.5',
                    'px-4 py-2 rounded-[var(--radius-full)]',
                    'text-sm font-[family-name:var(--font-heading)] font-semibold',
                    'transition-all duration-[var(--duration-fast)]',
                    'min-h-[var(--touch-min)] cursor-pointer',
                    'whitespace-nowrap',
                    isActive
                      ? 'bg-accent-primary text-text-inverted shadow-sm'
                      : 'bg-surface-card text-text-secondary border border-border-default hover:bg-surface-sunken',
                  )}
                >
                  {allFilled && (
                    <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
                  )}
                  {tab}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ---- Main content ---- */}
      <main className="flex-1 overflow-y-auto px-5 py-6 mx-auto w-full max-w-3xl">
        {/* Local draft restored banner */}
        {restoredFromLocal && (
          <div
            className={cn(
              'mb-4 px-4 py-3 rounded-[var(--radius-md)]',
              'bg-status-info-light border border-status-info/20',
              'flex items-center gap-3',
            )}
          >
            <HardDrive className="h-5 w-5 text-status-info shrink-0" strokeWidth={1.5} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-text-primary">
                Bozza locale ripristinata
              </p>
              <p className="text-xs text-text-secondary mt-0.5">
                {isOnline
                  ? 'I dati verranno sincronizzati con il server al prossimo salvataggio.'
                  : 'Sei offline. I dati verranno sincronizzati quando torni online.'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setRestoredFromLocal(false)}
              className="text-text-muted hover:text-text-primary transition-colors text-xs font-medium shrink-0 cursor-pointer"
            >
              Chiudi
            </button>
          </div>
        )}

        {/* Sezione attiva — campi sempre espansi, niente accordion */}
        {sections
          .filter((s) => sectionTabs.length <= 1 || s.name === activeTab)
          .map((section) => (
            <div key={section.name} className="space-y-5">
              {section.fields.map((field) => (
                <DynamicField
                  key={field.id}
                  field={field}
                  value={answers[field.fieldKey]}
                  onChange={(val) => updateAnswer(field.fieldKey, val)}
                  onBlur={saveOnBlur}
                  disabled={isCompleted}
                />
              ))}
            </div>
          ))}

        {/* ---- Measurements section ---- */}
        {measurements.length > 0 && (
          <section className="mt-8">
            <h3 className="font-[family-name:var(--font-heading)] text-lg font-bold text-text-primary mb-4">
              Misure
            </h3>
            <div className="space-y-4">
              {measurements.map((m) => (
                <MeasurementSlider
                  key={m.key}
                  measurement={m}
                  onChange={(val) => updateMeasurement(m.key, val)}
                  disabled={isCompleted}
                />
              ))}
            </div>
          </section>
        )}

        {/* ---- Bottom CTAs ---- */}
        <div className="mt-10 space-y-3 pb-8">
          {!isCompleted && (
            <Button
              size="lg"
              fullWidth
              disabled={!allRequiredFilled || isCompleting}
              loading={isCompleting}
              leftIcon={<CheckCircle strokeWidth={2} />}
              onClick={handleComplete}
            >
              Completa Check-up
            </Button>
          )}

          <Button
            variant={isCompleted ? 'primary' : 'secondary'}
            size="lg"
            fullWidth
            disabled={!isCompleted}
            leftIcon={<Sparkles strokeWidth={2} />}
            onClick={() => {
              if (id) navigate(`/checkups/${id}/pride`);
            }}
          >
            Genera Pride
          </Button>
        </div>
      </main>
    </div>
  );
}

/* ==========================================================================
   Sub-components
   ========================================================================== */

/* --------------------------------------------------------------------------
   Save indicator
   -------------------------------------------------------------------------- */

function SaveIndicator({ status }: { status: 'idle' | 'saving' | 'saved' | 'error' | 'local' }) {
  if (status === 'idle') return null;

  return (
    <div
      className={cn(
        'flex items-center gap-1.5 px-2 py-1',
        'rounded-[var(--radius-full)]',
        'text-xs font-medium',
        'transition-all duration-[var(--duration-normal)]',
        status === 'saving' && 'text-text-muted',
        status === 'saved' && 'text-status-positive',
        status === 'error' && 'text-status-negative',
        status === 'local' && 'text-status-info',
      )}
    >
      {status === 'saving' && (
        <>
          <Cloud className="h-4 w-4 animate-pulse" strokeWidth={1.5} />
          <span>Salvataggio...</span>
        </>
      )}
      {status === 'saved' && (
        <>
          <Cloud className="h-4 w-4" strokeWidth={1.5} />
          <span>Salvato</span>
        </>
      )}
      {status === 'error' && (
        <>
          <CloudOff className="h-4 w-4" strokeWidth={1.5} />
          <span>Errore</span>
        </>
      )}
      {status === 'local' && (
        <>
          <HardDrive className="h-4 w-4" strokeWidth={1.5} />
          <span>Salvato localmente</span>
        </>
      )}
    </div>
  );
}

/* --------------------------------------------------------------------------
   Header skeleton
   -------------------------------------------------------------------------- */

function CheckupHeaderSkeleton() {
  return (
    <header
      className={cn(
        'sticky top-0 z-[var(--z-sticky)]',
        'bg-surface-base/80 backdrop-blur-lg',
        'border-b border-border-default',
      )}
    >
      <div className="flex items-center gap-3 h-16 px-5 mx-auto max-w-3xl">
        <Skeleton width={48} height={48} className="rounded-[var(--radius-md)]" />
        <div className="flex-1 space-y-2">
          <Skeleton height={16} className="w-2/5" />
          <Skeleton height={12} className="w-1/4" />
        </div>
      </div>
      <div className="h-0.5 bg-surface-sunken" />
    </header>
  );
}

/* --------------------------------------------------------------------------
   Dynamic field renderer
   -------------------------------------------------------------------------- */

interface DynamicFieldProps {
  field: FormField;
  value: unknown;
  onChange: (value: unknown) => void;
  onBlur: () => void;
  disabled?: boolean;
}

function DynamicField({ field, value, onChange, onBlur, disabled }: DynamicFieldProps) {
  const label = field.isRequired ? `${field.label} *` : field.label;

  switch (field.fieldType) {
    case 'text':
      return (
        <Input
          label={label}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          placeholder={field.placeholder ?? ''}
          helperText={field.helpText}
          disabled={disabled}
        />
      );

    case 'textarea':
      return (
        <TextareaField
          label={label}
          value={(value as string) ?? ''}
          onChange={(val) => onChange(val)}
          onBlur={onBlur}
          placeholder={field.placeholder}
          helpText={field.helpText}
          disabled={disabled}
        />
      );

    case 'select':
      return (
        <SelectField
          label={label}
          value={(value as string) ?? ''}
          options={normalizeOptions(field.options)}
          onChange={(val) => onChange(val)}
          onBlur={onBlur}
          helpText={field.helpText}
          disabled={disabled}
        />
      );

    case 'multi_select':
      return (
        <MultiSelectField
          label={label}
          value={(value as string[]) ?? []}
          options={normalizeOptions(field.options)}
          onChange={(val) => onChange(val)}
          helpText={field.helpText}
          disabled={disabled}
        />
      );

    case 'number':
      return (
        <NumberField
          label={label}
          value={value as number | null}
          onChange={(val) => onChange(val)}
          onBlur={onBlur}
          min={field.validationRules?.min}
          max={field.validationRules?.max}
          placeholder={field.placeholder}
          helpText={field.helpText}
          disabled={disabled}
        />
      );

    case 'boolean':
      return (
        <ToggleField
          label={label}
          value={Boolean(value)}
          onChange={(val) => onChange(val)}
          helpText={field.helpText}
          disabled={disabled}
        />
      );

    case 'date':
      return (
        <Input
          label={label}
          type="date"
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          helperText={field.helpText}
          disabled={disabled}
        />
      );

    case 'photos':
      return (
        <PhotosField
          label={label}
          value={(value as string[]) ?? []}
          onChange={(val) => onChange(val)}
          helpText={field.helpText}
          disabled={disabled}
        />
      );

    case 'body_map':
      return (
        <BodyMapField
          label={label}
          value={(value as string[]) ?? []}
          options={normalizeOptions(field.options).map((o) => o.value)}
          onChange={(val) => onChange(val)}
          helpText={field.helpText}
          disabled={disabled}
        />
      );

    default:
      return (
        <Input
          label={label}
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          disabled={disabled}
        />
      );
  }
}

/* --------------------------------------------------------------------------
   Textarea field with voice button
   -------------------------------------------------------------------------- */

function TextareaField({
  label,
  value,
  onChange,
  onBlur,
  placeholder,
  helpText,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (val: string) => void;
  onBlur: () => void;
  placeholder?: string;
  helpText?: string;
  disabled?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1.5 w-full">
      <label
        className={cn(
          'font-[family-name:var(--font-heading)] text-sm font-semibold',
          'text-text-secondary',
        )}
      >
        {label}
      </label>
      <div className="relative">
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          placeholder={placeholder ?? 'Inserisci note...'}
          disabled={disabled}
          rows={3}
          className={cn(
            'w-full rounded-[var(--radius-md)]',
            'bg-surface-card border border-border-default',
            'px-4 py-3 pr-14 text-base text-text-primary',
            'font-[family-name:var(--font-body)]',
            'placeholder:text-text-muted',
            'transition-all duration-[var(--duration-normal)] ease-[var(--ease-out)]',
            'focus:border-border-focus focus:ring-2 focus:ring-accent-primary-light',
            'focus:outline-none',
            'hover:border-border-hover',
            'disabled:bg-surface-sunken disabled:text-text-muted disabled:cursor-not-allowed',
            'resize-y min-h-[80px]',
          )}
        />
        {/* Voice button */}
        {!disabled && (
          <button
            type="button"
            onClick={() => {
              /* Future: voice-to-text */
            }}
            className={cn(
              'absolute right-3 top-3',
              'flex items-center justify-center',
              'h-10 w-10 rounded-[var(--radius-md)]',
              'bg-accent-primary-light text-accent-primary',
              'transition-all duration-[var(--duration-fast)]',
              'hover:bg-accent-primary-medium active:scale-95',
              'cursor-pointer',
            )}
            aria-label="Dettatura vocale"
          >
            <Mic className="h-5 w-5" strokeWidth={1.5} />
          </button>
        )}
      </div>
      {helpText && (
        <p className="text-sm text-text-muted">{helpText}</p>
      )}
    </div>
  );
}

/* --------------------------------------------------------------------------
   Normalizza le opzioni: il backend salva i form_fields.options come array di
   stringhe semplici (es. ["Acne", "Psoriasi"]) ma SelectField/MultiSelectField
   si aspettano oggetti FormFieldOption {value, label}. Senza questa normalizzazione
   tutti i bottoni sono renderizzati con label/value undefined → caselle bianche.
   Accetta entrambi i formati per non rompere se in futuro alcuni campi useranno
   il formato esteso.
   -------------------------------------------------------------------------- */

function normalizeOptions(
  raw: unknown,
): FormFieldOption[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((o): FormFieldOption | null => {
      if (typeof o === 'string') return { value: o, label: o };
      if (o && typeof o === 'object' && 'value' in o) {
        const obj = o as { value: unknown; label?: unknown };
        return {
          value: String(obj.value),
          label: obj.label != null ? String(obj.label) : String(obj.value),
        };
      }
      return null;
    })
    .filter((o): o is FormFieldOption => o !== null);
}

/* --------------------------------------------------------------------------
   Select field
   -------------------------------------------------------------------------- */

function SelectField({
  label,
  value,
  options,
  onChange,
  onBlur,
  helpText,
  disabled,
}: {
  label: string;
  value: string;
  options: FormFieldOption[];
  onChange: (val: string) => void;
  onBlur: () => void;
  helpText?: string;
  disabled?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1.5 w-full">
      <label
        className={cn(
          'font-[family-name:var(--font-heading)] text-sm font-semibold',
          'text-text-secondary',
        )}
      >
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        disabled={disabled}
        className={cn(
          'h-[var(--touch-min)] w-full rounded-[var(--radius-md)]',
          'bg-surface-card border border-border-default',
          'px-4 text-base text-text-primary',
          'font-[family-name:var(--font-body)]',
          'transition-all duration-[var(--duration-normal)] ease-[var(--ease-out)]',
          'focus:border-border-focus focus:ring-2 focus:ring-accent-primary-light',
          'focus:outline-none',
          'hover:border-border-hover',
          'disabled:bg-surface-sunken disabled:text-text-muted disabled:cursor-not-allowed',
          'cursor-pointer',
          !value && 'text-text-muted',
        )}
      >
        <option value="">Seleziona...</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      {helpText && (
        <p className="text-sm text-text-muted">{helpText}</p>
      )}
    </div>
  );
}

/* --------------------------------------------------------------------------
   Multi-select (checkbox group)
   -------------------------------------------------------------------------- */

function MultiSelectField({
  label,
  value,
  options,
  onChange,
  helpText,
  disabled,
}: {
  label: string;
  value: string[];
  options: FormFieldOption[];
  onChange: (val: string[]) => void;
  helpText?: string;
  disabled?: boolean;
}) {
  const handleToggle = (optValue: string) => {
    if (value.includes(optValue)) {
      onChange(value.filter((v) => v !== optValue));
    } else {
      onChange([...value, optValue]);
    }
  };

  return (
    <div className="flex flex-col gap-1.5 w-full">
      <label
        className={cn(
          'font-[family-name:var(--font-heading)] text-sm font-semibold',
          'text-text-secondary',
        )}
      >
        {label}
      </label>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const selected = value.includes(opt.value);
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => !disabled && handleToggle(opt.value)}
              disabled={disabled}
              className={cn(
                'inline-flex items-center gap-1.5',
                'px-4 py-2.5 rounded-[var(--radius-md)]',
                'text-sm font-medium',
                'border transition-all duration-[var(--duration-fast)]',
                'min-h-[var(--touch-min)] cursor-pointer',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                selected
                  ? 'bg-accent-primary text-text-inverted border-accent-primary shadow-sm'
                  : 'bg-surface-card text-text-primary border-border-default hover:border-border-hover',
              )}
            >
              {selected && <Check className="h-4 w-4" strokeWidth={2.5} />}
              {opt.label}
            </button>
          );
        })}
      </div>
      {helpText && (
        <p className="text-sm text-text-muted mt-1">{helpText}</p>
      )}
    </div>
  );
}

/* --------------------------------------------------------------------------
   Number field
   -------------------------------------------------------------------------- */

function NumberField({
  label,
  value,
  onChange,
  onBlur,
  min,
  max,
  placeholder,
  helpText,
  disabled,
}: {
  label: string;
  value: number | null;
  onChange: (val: number | null) => void;
  onBlur: () => void;
  min?: number;
  max?: number;
  placeholder?: string;
  helpText?: string;
  disabled?: boolean;
}) {
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === '') {
      onChange(null);
      return;
    }
    const num = parseFloat(raw);
    if (!isNaN(num)) {
      onChange(num);
    }
  };

  return (
    <Input
      label={label}
      type="number"
      inputMode="decimal"
      value={value !== null && value !== undefined ? String(value) : ''}
      onChange={handleChange}
      onBlur={onBlur}
      min={min}
      max={max}
      placeholder={placeholder ?? ''}
      helperText={helpText}
      disabled={disabled}
    />
  );
}

/* --------------------------------------------------------------------------
   Toggle (boolean) field
   -------------------------------------------------------------------------- */

function ToggleField({
  label,
  value,
  onChange,
  helpText,
  disabled,
}: {
  label: string;
  value: boolean;
  onChange: (val: boolean) => void;
  helpText?: string;
  disabled?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1.5 w-full">
      <div className="flex items-center justify-between min-h-[var(--touch-min)]">
        <label
          className={cn(
            'font-[family-name:var(--font-heading)] text-sm font-semibold',
            'text-text-secondary',
            'flex-1',
          )}
        >
          {label}
        </label>
        <button
          type="button"
          role="switch"
          aria-checked={value}
          onClick={() => !disabled && onChange(!value)}
          disabled={disabled}
          className={cn(
            'relative inline-flex h-7 w-12 shrink-0',
            'items-center rounded-full',
            'transition-colors duration-[var(--duration-normal)] ease-[var(--ease-out)]',
            'cursor-pointer',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            value ? 'bg-accent-primary' : 'bg-border-default',
          )}
        >
          <span
            className={cn(
              'inline-block h-5 w-5 rounded-full bg-white shadow-sm',
              'transition-transform duration-[var(--duration-normal)] ease-[var(--ease-out)]',
              value ? 'translate-x-6' : 'translate-x-1',
            )}
          />
        </button>
      </div>
      {helpText && (
        <p className="text-sm text-text-muted">{helpText}</p>
      )}
    </div>
  );
}

/* --------------------------------------------------------------------------
   Photos field (placeholder grid)
   -------------------------------------------------------------------------- */

function PhotosField({
  label,
  value,
  helpText,
  disabled,
}: {
  label: string;
  value: string[];
  onChange: (val: string[]) => void;
  helpText?: string;
  disabled?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1.5 w-full">
      <label
        className={cn(
          'font-[family-name:var(--font-heading)] text-sm font-semibold',
          'text-text-secondary',
        )}
      >
        {label}
      </label>
      <div className="grid grid-cols-3 gap-3">
        {/* Existing photos */}
        {value.map((photoUrl, idx) => (
          <div
            key={idx}
            className={cn(
              'relative aspect-square rounded-[var(--radius-md)]',
              'border border-border-default bg-surface-sunken',
              'overflow-hidden',
            )}
          >
            <img
              src={photoUrl}
              alt={`Foto ${idx + 1}`}
              className="h-full w-full object-cover"
              loading="lazy"
            />
          </div>
        ))}

        {/* Add photo button */}
        {!disabled && (
          <button
            type="button"
            onClick={() => {
              /* Future: open camera or file picker */
            }}
            className={cn(
              'flex flex-col items-center justify-center gap-2',
              'aspect-square rounded-[var(--radius-md)]',
              'border-2 border-dashed border-border-default',
              'bg-surface-sunken',
              'text-text-muted',
              'transition-all duration-[var(--duration-fast)]',
              'hover:border-accent-primary hover:text-accent-primary hover:bg-accent-primary-light',
              'cursor-pointer',
              'min-h-[100px]',
            )}
          >
            <ImagePlus className="h-6 w-6" strokeWidth={1.5} />
            <span className="text-xs font-medium">Aggiungi</span>
          </button>
        )}
      </div>
      {helpText && (
        <p className="text-sm text-text-muted mt-1">{helpText}</p>
      )}
    </div>
  );
}

/* --------------------------------------------------------------------------
   Body map field (placeholder)
   -------------------------------------------------------------------------- */

function BodyMapField({
  label,
  value,
  options,
  onChange,
  helpText,
  disabled,
}: {
  label: string;
  value: string[];
  /** Macro-zone testuali ammesse (= form_field.options). */
  options: string[];
  onChange: (val: string[]) => void;
  helpText?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);

  // Variant disponibili in base alle option (es. solo body-back se la zona
  // sta solo dietro). Se l'admin ha aggiunto option non mappate in
  // bodyMapMacroZones.ts cadiamo su body-front come default.
  const variants = useMemo<MacroVariant[]>(() => {
    const v = getRequiredVariants(options);
    return v.length > 0 ? v : ['body-front'];
  }, [options]);

  const [activeVariant, setActiveVariant] = useState<MacroVariant>(variants[0]);

  // Re-allinea variant attiva se le option cambiano (es. campo diverso)
  useEffect(() => {
    if (!variants.includes(activeVariant)) {
      setActiveVariant(variants[0]);
    }
  }, [variants, activeVariant]);

  const zones = useMemo(
    () => buildZonesForOptions(options, activeVariant),
    [options, activeVariant],
  );

  const handleToggle = useCallback(
    (zoneId: string) => {
      if (value.includes(zoneId)) {
        onChange(value.filter((v) => v !== zoneId));
      } else {
        onChange([...value, zoneId]);
      }
    },
    [value, onChange],
  );

  return (
    <div className="flex flex-col gap-1.5 w-full">
      <label
        className={cn(
          'font-[family-name:var(--font-heading)] text-sm font-semibold',
          'text-text-secondary',
        )}
      >
        {label}
      </label>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(true)}
        className={cn(
          'flex items-center gap-4 w-full',
          'px-5 py-4 rounded-[var(--radius-lg)]',
          'border-2 border-dashed border-border-default',
          'bg-surface-card',
          'transition-all duration-[var(--duration-fast)]',
          'hover:border-accent-primary hover:shadow-sm',
          'cursor-pointer',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          'min-h-[var(--touch-comfortable)]',
        )}
      >
        <div
          className={cn(
            'flex h-12 w-12 items-center justify-center shrink-0',
            'rounded-[var(--radius-md)] bg-accent-primary-light',
          )}
        >
          <MapPin className="h-6 w-6 text-accent-primary" strokeWidth={1.5} />
        </div>
        <div className="flex-1 text-left min-w-0">
          <p className="font-[family-name:var(--font-heading)] text-sm font-semibold text-text-primary">
            {value.length > 0
              ? `${value.length} zone selezionate`
              : 'Seleziona zone'}
          </p>
          <p className="text-xs text-text-muted mt-0.5">
            Tocca per aprire la mappa corporea
          </p>
        </div>
        <ChevronRight className="h-5 w-5 text-text-muted shrink-0" strokeWidth={2} />
      </button>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-1">
          {value.map((zone) => (
            <span
              key={zone}
              className={cn(
                'inline-flex items-center px-2.5 py-1',
                'rounded-[var(--radius-full)]',
                'bg-accent-primary-light text-accent-primary',
                'text-xs font-medium',
              )}
            >
              {zone}
            </span>
          ))}
        </div>
      )}
      {helpText && (
        <p className="text-sm text-text-muted mt-1">{helpText}</p>
      )}

      {/* ---- Modal full-screen mappa corporea ---- */}
      {open && (
        <div className="fixed inset-0 z-[var(--z-modal,50)] flex flex-col bg-surface-base">
          {/* Header modal */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-border-default bg-surface-card">
            <div className="min-w-0">
              <p className="text-xs text-text-muted">Mappa corporea</p>
              <p className="font-[family-name:var(--font-heading)] text-base font-bold text-text-primary truncate">
                {label}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Chiudi"
              className="h-10 w-10 flex items-center justify-center rounded-full hover:bg-surface-sunken cursor-pointer shrink-0"
            >
              <X className="h-5 w-5 text-text-secondary" />
            </button>
          </div>

          {/* Tab front/back se entrambe necessarie */}
          {variants.length > 1 && (
            <div className="flex gap-2 px-5 py-3 border-b border-border-default">
              {variants.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setActiveVariant(v)}
                  className={cn(
                    'px-4 py-2 rounded-[var(--radius-full)] text-sm font-semibold cursor-pointer',
                    'transition-colors',
                    activeVariant === v
                      ? 'bg-accent-primary text-text-inverted'
                      : 'bg-surface-sunken text-text-secondary hover:bg-surface-card',
                  )}
                >
                  {v === 'body-front'
                    ? 'Vista frontale'
                    : v === 'body-back'
                      ? 'Vista posteriore'
                      : 'Viso'}
                </button>
              ))}
            </div>
          )}

          {/* SVG body */}
          <div className="flex-1 overflow-y-auto p-5 flex flex-col items-center">
            <BodyMap
              variant={activeVariant}
              selectedZones={value}
              onZoneToggle={handleToggle}
              zones={zones}
              readOnly={disabled}
            />
            {zones.length === 0 && (
              <p className="text-sm text-text-muted mt-6 text-center">
                Nessuna zona definita per questa vista.
              </p>
            )}
          </div>

          {/* Footer: chip selezionate + Conferma */}
          <div className="border-t border-border-default p-4 bg-surface-card">
            {value.length > 0 ? (
              <div className="flex flex-wrap gap-1.5 mb-3">
                {value.map((zone) => (
                  <button
                    key={zone}
                    type="button"
                    onClick={() => handleToggle(zone)}
                    className={cn(
                      'inline-flex items-center gap-1 px-2.5 py-1',
                      'rounded-[var(--radius-full)]',
                      'bg-accent-primary-light text-accent-primary',
                      'text-xs font-medium cursor-pointer',
                      'hover:bg-accent-primary hover:text-text-inverted',
                    )}
                  >
                    {zone}
                    <X className="h-3 w-3" />
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-text-muted mb-3 text-center">
                Tocca le zone sul corpo per selezionarle
              </p>
            )}
            <Button
              variant="primary"
              size="lg"
              fullWidth
              onClick={() => setOpen(false)}
            >
              Conferma
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/* --------------------------------------------------------------------------
   Measurement slider with delta
   -------------------------------------------------------------------------- */

function MeasurementSlider({
  measurement,
  onChange,
  disabled,
}: {
  measurement: MeasurementData;
  onChange: (val: number | null) => void;
  disabled?: boolean;
}) {
  const { label, value, previousValue, unit, min, max } = measurement;
  const current = value ?? min;

  // Delta calculation
  const delta =
    value !== null && previousValue !== null
      ? value - previousValue
      : null;

  const handleSliderChange = (e: ChangeEvent<HTMLInputElement>) => {
    const num = parseFloat(e.target.value);
    if (!isNaN(num)) onChange(num);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === '') {
      onChange(null);
      return;
    }
    const num = parseFloat(raw);
    if (!isNaN(num)) {
      const clamped = Math.min(Math.max(num, min), max);
      onChange(clamped);
    }
  };

  // Slider fill percentage
  const fillPct = ((current - min) / (max - min)) * 100;

  return (
    <div
      className={cn(
        'px-4 py-4 rounded-[var(--radius-lg)]',
        'border border-border-default bg-surface-card',
      )}
    >
      {/* Label row */}
      <div className="flex items-center justify-between mb-3">
        <span className="font-[family-name:var(--font-heading)] text-sm font-semibold text-text-primary">
          {label}
        </span>

        <div className="flex items-center gap-2">
          {/* Numeric input */}
          <input
            type="number"
            value={value !== null ? value : ''}
            onChange={handleInputChange}
            disabled={disabled}
            min={min}
            max={max}
            step={0.5}
            className={cn(
              'w-20 h-9 rounded-[var(--radius-sm)]',
              'border border-border-default bg-surface-card',
              'px-2 text-center text-base font-bold text-text-primary tabular-nums',
              'font-[family-name:var(--font-heading)]',
              'focus:border-border-focus focus:ring-2 focus:ring-accent-primary-light',
              'focus:outline-none',
              'disabled:bg-surface-sunken disabled:text-text-muted',
            )}
          />
          <span className="text-sm text-text-muted font-medium">{unit}</span>

          {/* Delta indicator */}
          {delta !== null && (
            <DeltaIndicator delta={delta} unit={unit} />
          )}
        </div>
      </div>

      {/* Slider */}
      <div className="relative">
        <input
          type="range"
          min={min}
          max={max}
          step={0.5}
          value={current}
          onChange={handleSliderChange}
          disabled={disabled}
          className="measurement-slider w-full"
          style={
            {
              '--fill-pct': `${fillPct}%`,
            } as CSSProperties
          }
        />
        {/* Min / Max labels */}
        <div className="flex justify-between mt-1">
          <span className="text-xs text-text-muted tabular-nums">{min}</span>
          <span className="text-xs text-text-muted tabular-nums">{max}</span>
        </div>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------------
   Delta indicator
   -------------------------------------------------------------------------- */

function DeltaIndicator({ delta }: { delta: number; unit?: string }) {
  if (delta === 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-xs font-medium text-text-muted">
        <Minus className="h-3 w-3" strokeWidth={2} />
        0
      </span>
    );
  }

  // Positive delta = increase, negative = decrease
  // For body measurements, decrease is typically "improvement" (green)
  const isImprovement = delta < 0;
  const absDelta = Math.abs(delta).toFixed(1);

  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 text-xs font-bold',
        isImprovement ? 'text-status-positive' : 'text-status-negative',
      )}
    >
      {isImprovement ? (
        <TrendingDown className="h-3.5 w-3.5" strokeWidth={2} />
      ) : (
        <TrendingUp className="h-3.5 w-3.5" strokeWidth={2} />
      )}
      {isImprovement ? '-' : '+'}
      {absDelta}
    </span>
  );
}
